import type {
  MessageDirectionName,
  MessageStatusName,
  ReportMessageDetail,
  ReportMessageTimelineEvent,
} from "@multi-chat/shared";
import {
  localizeDeliveryFailureReason,
  resolveWhatsAppDeliveryFailureCopy,
} from "../whatsapp/whatsapp-delivery-failure-presentation.js";
import { statusLabelKey } from "./outgoing-report-presentation.js";

type ReportMessageDetailFailure = NonNullable<ReportMessageDetail["currentFailure"]>;

export function reportDetailDirectionLabelKey(direction: MessageDirectionName): string {
  switch (direction) {
    case "INBOUND":
      return "reports.detail.directions.INBOUND";
    case "OUTBOUND":
      return "reports.detail.directions.OUTBOUND";
    default: {
      const _exhaustive: never = direction;
      return _exhaustive;
    }
  }
}

export function reportDetailIdentityLabelKey(direction: MessageDirectionName): string {
  switch (direction) {
    case "INBOUND":
      return "reports.detail.identity.sender";
    case "OUTBOUND":
      return "reports.detail.identity.recipient";
    default: {
      const _exhaustive: never = direction;
      return _exhaustive;
    }
  }
}

export function timelineEventLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  event: ReportMessageTimelineEvent,
  direction: MessageDirectionName,
): string {
  switch (event.kind) {
    case "CREATED":
      return direction === "INBOUND"
        ? t("reports.detail.timeline.received")
        : t("reports.detail.timeline.created");
    case "QUEUED":
      return t("reports.detail.timeline.queued");
    case "STATUS":
      if (event.status == null) return t("reports.detail.timeline.unknown");
      return t(statusLabelKey(event.status as MessageStatusName));
    default: {
      const _exhaustive: never = event.kind;
      return _exhaustive;
    }
  }
}

export function detailPrimaryContent(detail: ReportMessageDetail): string | null {
  if (detail.template?.renderedBody?.trim()) return detail.template.renderedBody;
  if (detail.content?.trim()) return detail.content;
  return null;
}

export async function copyReportIdentifier(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

export type ReportFailurePresentation = {
  primary: string;
  secondary: string | null;
  technicalCode: string | null;
};

export function presentReportCurrentFailure(
  t: (key: string, options?: Record<string, unknown>) => string,
  failure: ReportMessageDetailFailure,
  unknownLabel: string,
): ReportFailurePresentation {
  const code = failure.code?.trim() || null;
  if (code) {
    const copy = resolveWhatsAppDeliveryFailureCopy(t, code);
    if (copy) {
      return {
        primary: copy.primary,
        secondary: copy.secondary,
        technicalCode: copy.technicalCode,
      };
    }
  }
  const reason = failure.reason?.trim() || null;
  if (reason) {
    return {
      primary: localizeDeliveryFailureReason(t, reason) ?? reason,
      secondary: null,
      technicalCode: code,
    };
  }
  return { primary: unknownLabel, secondary: null, technicalCode: code };
}

/** Timeline row: prefer localized code copy; never lead with raw Meta English title. */
export function presentTimelineFailureText(
  t: (key: string, options?: Record<string, unknown>) => string,
  event: Pick<ReportMessageTimelineEvent, "errorCode" | "errorMessage">,
): string | null {
  if (event.errorCode) {
    const copy = resolveWhatsAppDeliveryFailureCopy(t, event.errorCode);
    if (copy) return copy.primary;
  }
  if (event.errorMessage) {
    return localizeDeliveryFailureReason(t, event.errorMessage) ?? event.errorMessage;
  }
  return null;
}
